/** Turns an API URL into the resource (and optional action) a loader can name. */

const SKIP_SEGMENTS = new Set(['api', 'v1', 'rmm', 'pages', 'tenant', 'admin']);

const GENERIC_SEGMENTS = new Set([
  'composition',
  'data',
  'items',
  'list',
  'detail',
  'records',
  'record',
  'entity',
  'entities',
]);

const KNOWN_RESOURCES = new Set([
  'customers',
  'contacts',
  'invoices',
  'subscriptions',
  'products',
  'services',
  'assets',
  'suppliers',
  'orders',
  'tickets',
  'tasks',
  'connectivity',
  'hosting',
  'domains',
  'users',
  'partners',
  'tenants',
  'agreements',
  'quotes',
  'locations',
  'devices',
  'metrics',
  'backups',
  'deployments',
  'integrations',
  'dashboard',
  'assignments',
  'licenses',
  'accounts',
  'templates',
  'rates',
  'destinations',
  'policies',
  'incidents',
  'networks',
  'addresses',
  'subnets',
  'workspaces',
  'roles',
  'permissions',
  'settings',
  'profile',
  'calls',
  'comments',
  'attachments',
  'notes',
  'documents',
  'files',
  'reports',
  'billing',
  'usage',
  'channels',
  'security',
  'audit',
  'events',
  'timeline',
  'overview',
  'catalog',
  'prices',
  'price-plans',
  'product-agreements',
  'service-assignments',
  'effective-access',
  'internet-access',
  'ms365',
  'portaone',
  'xelion',
  'eset',
  'plesk',
  'devman',
  'ipam',
  'support',
  'sla',
  'mobile',
  'auth',
]);

const ACTION_SEGMENTS = new Set([
  'preview',
  'mutate',
  'link',
  'terminate',
  'location',
  'matches',
  'catalog',
  'extract',
  'settings',
  'merge',
  'search',
  'export',
  'import',
  'test',
  'validate',
  'count',
  'summary',
  'sync',
  'refresh',
  'assign',
  'unassign',
  'enable',
  'disable',
  'activate',
  'deactivate',
  'upload',
  'download',
  'generate',
  'send',
  'invite',
  'reset',
  'verify',
  'lookup',
  'check',
  'status',
  'create',
  'update',
  'delete',
  'save',
  'poll',
]);

const NOISE_PATH = /\/(health|notifications|images|icons)(\/|$)|\/auth\/(refresh|me)(\/|$)|\/i18n\//;

export interface PortalRequestParts {
  resource: string;
  action: string | null;
}

export function isPortalApiUrl(url: string): boolean {
  const path = stripQuery(url);
  return path.includes('/api/v1') || path.startsWith('api/v1');
}

export function portalRequestParts(url: string): PortalRequestParts | null {
  const path = stripQuery(url).replace(/^https?:\/\/[^/]+/i, '');
  if (!isPortalApiUrl(path) || NOISE_PATH.test(path)) {
    return null;
  }
  const raw = path.split('/').filter(Boolean);
  const v1 = raw.lastIndexOf('v1');
  const segments = (v1 >= 0 ? raw.slice(v1 + 1) : raw).filter(
    (segment) => !SKIP_SEGMENTS.has(segment) && !isIdSegment(segment),
  );
  if (!segments.length) {
    return null;
  }

  let action: string | null = null;
  let pool = segments;
  const tail = segments[segments.length - 1];
  if (ACTION_SEGMENTS.has(tail)) {
    action = tail;
    pool = segments.slice(0, -1);
  } else if (GENERIC_SEGMENTS.has(tail)) {
    pool = segments.slice(0, -1);
  }

  const known = [...pool].reverse().find((segment) => KNOWN_RESOURCES.has(segment));
  const resource =
    known ||
    [...pool].reverse().find((segment) => !GENERIC_SEGMENTS.has(segment) && !ACTION_SEGMENTS.has(segment) && !looksLikeSlug(segment)) ||
    pool[pool.length - 1] ||
    segments[0];

  if (!resource) {
    return null;
  }
  return { resource, action };
}

function stripQuery(url: string): string {
  return (url || '').split('?')[0];
}

function looksLikeSlug(segment: string): boolean {
  return segment.includes('-') && !KNOWN_RESOURCES.has(segment);
}

function isIdSegment(segment: string): boolean {
  if (/^\d+$/.test(segment)) {
    return true;
  }
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
    return true;
  }
  if (/^[0-9a-f]{16,}$/i.test(segment)) {
    return true;
  }
  return false;
}
